import React, { useState } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Drawer, useTheme, useMediaQuery, Collapse, Tooltip, Typography, Avatar } from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate, useLocation } from 'react-router-dom';
interface SidebarProps {
  open: boolean;
  onClose: () => void;
  activePage?: string;
}
const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  activePage = 'dashboard'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = localStorage.getItem('userRole') || 'maker';
  const username = localStorage.getItem('username') || 'admin';
  const isChecker = userRole === 'checker';
  const [partnersOpen, setPartnersOpen] = useState(activePage === 'partners' || activePage === 'partner-users');
  const [subAgentsOpen, setSubAgentsOpen] = useState(activePage === 'sub-agents' || activePage === 'sub-agent-users');
  const [branchUsersOpen, setBranchUsersOpen] = useState(activePage === 'branch-users' || activePage === 'user-management' || activePage === 'bulk-upload' || activePage === 'bulk-upload-lists' || activePage === 'maker-inbox' || activePage === 'checker-inbox');
  const drawerWidth = 240;
  // Checker only sees: Dashboard, Transactions, Checker - Amendments
  const allMenuItems = [{
    text: 'Dashboard',
    icon: 'lucide:layout-dashboard',
    path: '/',
    id: 'dashboard',
    roles: ['maker', 'checker']
  }, {
    text: 'Transactions Summary',
    icon: 'lucide:list-checks',
    path: '/enhanced-transactions',
    id: 'enhanced-transactions',
    roles: ['maker', 'checker']
  }, {
    text: 'RTGS Transactions',
    icon: 'lucide:building-2',
    path: '/rtgs',
    id: 'rtgs',
    roles: ['maker']
  }, {
    text: 'Transaction Reversal',
    icon: 'lucide:undo-2',
    path: '/transaction-reversal',
    id: 'transaction-reversal',
    roles: ['maker']
  }, {
    text: 'Reversal Lists',
    icon: 'lucide:list',
    path: '/reversal-lists',
    id: 'reversal-lists',
    roles: ['maker']
  }, {
    text: 'e-PRC Generation',
    icon: 'lucide:file-text',
    path: '/eprc-generation',
    id: 'eprc-generation',
    roles: ['maker']
  }, {
    text: 'Compliance Cases',
    icon: 'lucide:briefcase',
    path: '/compliance-cases',
    id: 'compliance-cases',
    roles: ['maker']
  }, {
    text: 'Funding & Settlement',
    icon: 'lucide:landmark',
    path: '/funding-settlement',
    id: 'funding-settlement',
    roles: ['maker']
  }, {
    text: 'Corridor Governance',
    icon: 'lucide:route',
    path: '/corridor-governance',
    id: 'corridor-governance',
    roles: ['maker']
  }, {
    text: 'FX Management',
    icon: 'lucide:circle-dollar-sign',
    path: '/fx-management',
    id: 'fx-management',
    roles: ['maker']
  }, {
    text: 'Reporting & Exports',
    icon: 'lucide:file-bar-chart',
    path: '/reporting-exports',
    id: 'reporting-exports',
    roles: ['maker']
  }];
  const menuItems = allMenuItems.filter(item => item.roles.includes(userRole));
  const handleNavigation = (path: string) => {
    // Close the mobile drawer if open
    if (isMobile) {
      onClose();
    }
    navigate(path);
  };
  const togglePartners = () => {
    setPartnersOpen(!partnersOpen);
  };
  const toggleSubAgents = () => {
    setSubAgentsOpen(!subAgentsOpen);
  };
  const toggleBranchUsers = () => {
    setBranchUsersOpen(!branchUsersOpen);
  };
  const drawerContent = <Box sx={{
    width: drawerWidth,
    pt: 1,
    height: '100%',
    backgroundColor: '#F8F9FA',
    borderRight: '1px solid #E5E7EB',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  }} data-id="element-1827">
      <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      py: 2
    }} data-id="element-1828">
        <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit Logo" sx={{
        height: 40,
        transition: 'all 200ms ease'
      }} data-id="element-1829" />
      </Box>
      <Box sx={{
      px: 2,
      mb: 2
    }} data-id="element-1830">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        p: 1.5,
        borderRadius: 2,
        background: '#F0F3F7',
        border: '1px solid #E5E7EB'
      }} data-id="element-1831">
          <Avatar sx={{
          width: 38,
          height: 38,
          bgcolor: 'white',
          color: isChecker ? '#0B7923' : '#0052CC',
          fontWeight: 600,
          border: '2px solid',
          borderColor: isChecker ? '#0B7923' : '#0052CC'
        }} data-id="element-1832">
            {isChecker ? 'C' : 'A'}
          </Avatar>
          <Box sx={{
          ml: 1.5
        }} data-id="element-1833">
            <Typography variant="subtitle2" sx={{
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.2
          }} data-id="element-1834">
              {isChecker ? 'Checker User' : 'Admin User'}
            </Typography>
            <Typography variant="caption" sx={{
            color: 'text.secondary',
            fontSize: '0.7rem'
          }} data-id="element-1835">
              {isChecker ? 'Amendment Checker' : 'Super Admin'}
            </Typography>
          </Box>
        </Box>
      </Box>
      <List sx={{
      mt: 1,
      px: 1.5,
      flex: 1,
      overflow: 'auto'
    }} data-id="element-1836">
        {menuItems.map(item => <ListItem key={item.text} disablePadding sx={{
        mb: 0.5
      }} data-id="element-1837">
            <Tooltip title={item.text} placement="right" arrow data-id="element-1838">
              <ListItemButton sx={{
            borderRadius: 2,
            py: 1.2,
            backgroundColor: item.id === activePage ? '#F0F3F7' : 'transparent',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 200ms ease',
            '&:hover': {
              backgroundColor: '#F9FAFB'
            },
            '&::before': item.id === activePage ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              background: '#0052CC'
            } : {}
          }} onClick={() => handleNavigation(item.path)} data-id="element-1839">
                <ListItemIcon sx={{
              minWidth: 36,
              color: item.id === activePage ? '#0052CC' : theme.palette.text.secondary,
              '& svg': {
                transition: 'all 200ms ease'
              }
            }} data-id="element-1840">
                  <Icon icon={item.icon} width={20} height={20} data-id="element-1841" />
                </ListItemIcon>
                <ListItemText primary={item.text} sx={{
              '& .MuiTypography-root': {
                fontWeight: item.id === activePage ? 600 : 500,
                color: item.id === activePage ? '#0052CC' : theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '14px'
              }
            }} data-id="element-1842" />
              </ListItemButton>
            </Tooltip>
          </ListItem>)}
        {/* Branch Users Group - only show for maker */}
        {!isChecker && <>
            <ListItem disablePadding sx={{
          mb: 0.5
        }} data-id="element-1843">
              <ListItemButton sx={{
            borderRadius: 2,
            py: 1.2,
            backgroundColor: activePage === 'user-management' || activePage === 'bulk-upload' || activePage === 'bulk-upload-lists' || activePage === 'maker-inbox' || activePage === 'checker-inbox' ? '#F0F3F7' : 'transparent',
            position: 'relative',
            transition: 'all 200ms ease',
            '&:hover': {
              backgroundColor: '#F9FAFB'
            },
            '&::before': activePage === 'user-management' || activePage === 'bulk-upload' || activePage === 'bulk-upload-lists' || activePage === 'maker-inbox' || activePage === 'checker-inbox' ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              background: '#0052CC'
            } : {}
          }} onClick={toggleBranchUsers} data-id="element-1844">
                <ListItemIcon sx={{
              minWidth: 36,
              color: activePage === 'user-management' || activePage === 'bulk-upload' || activePage === 'bulk-upload-lists' || activePage === 'maker-inbox' || activePage === 'checker-inbox' ? '#0052CC' : theme.palette.text.secondary,
              '& svg': {
                transition: 'all 200ms ease'
              }
            }} data-id="element-1845">
                  <Icon icon="lucide:users" width={20} height={20} data-id="element-1846" />
                </ListItemIcon>
                <ListItemText primary="Branch Records" sx={{
              '& .MuiTypography-root': {
                fontWeight: activePage === 'user-management' || activePage === 'bulk-upload' || activePage === 'bulk-upload-lists' || activePage === 'maker-inbox' || activePage === 'checker-inbox' ? 600 : 500,
                color: activePage === 'user-management' || activePage === 'bulk-upload' || activePage === 'bulk-upload-lists' || activePage === 'maker-inbox' || activePage === 'checker-inbox' ? '#0052CC' : theme.palette.text.primary,
                fontSize: '14px'
              }
            }} data-id="element-1847" />
                {branchUsersOpen ? <Icon icon="lucide:chevron-up" width={18} height={18} color={theme.palette.text.secondary} data-id="element-1848" /> : <Icon icon="lucide:chevron-down" width={18} height={18} color={theme.palette.text.secondary} data-id="element-1849" />}
              </ListItemButton>
            </ListItem>
            <Collapse in={branchUsersOpen} timeout="auto" unmountOnExit data-id="element-1850">
              <List component="div" disablePadding sx={{
            ml: 1,
            mb: 0.5
          }} data-id="element-1851">
                <ListItem disablePadding data-id="element-1852">
                  <ListItemButton sx={{
                pl: 4,
                py: 1,
                borderRadius: 2,
                backgroundColor: activePage === 'user-management' ? '#F0F3F7' : 'transparent',
                position: 'relative',
                '&:hover': {
                  backgroundColor: activePage === 'user-management' ? '#F9FAFB' : 'rgba(79, 70, 229, 0.04)'
                },
                '&::before': activePage === 'user-management' ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: '#0052CC'
                } : {},
                transition: 'all 200ms ease',
                '&:hover': {
                  transform: 'translateX(3px)'
                }
              }} onClick={() => handleNavigation('/branch-users/user-management')} data-id="element-1853">
                    <ListItemIcon sx={{
                  minWidth: 28,
                  color: activePage === 'user-management' ? '#0052CC' : theme.palette.text.secondary,
                  '& svg': {
                    filter: activePage === 'user-management' ? 'drop-shadow(0 0 6px rgba(79, 70, 229, 0.5))' : 'none'
                  }
                }} data-id="element-1854">
                      <Icon icon="lucide:user-cog" width={16} height={16} data-id="element-1855" />
                    </ListItemIcon>
                    <ListItemText primary="User Management" sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.85rem',
                    fontWeight: activePage === 'user-management' ? 600 : 500,
                    color: activePage === 'user-management' ? '#0052CC' : theme.palette.text.primary
                  }
                }} data-id="element-1856" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding data-id="element-1857">
                  <ListItemButton sx={{
                pl: 4,
                py: 1,
                borderRadius: 2,
                backgroundColor: activePage === 'bulk-upload' ? '#F0F3F7' : 'transparent',
                position: 'relative',
                '&:hover': {
                  backgroundColor: activePage === 'bulk-upload' ? '#F9FAFB' : 'rgba(79, 70, 229, 0.04)'
                },
                '&::before': activePage === 'bulk-upload' ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: '#0052CC'
                } : {},
                transition: 'all 200ms ease',
                '&:hover': {
                  transform: 'translateX(3px)'
                }
              }} onClick={() => handleNavigation('/branch-users/bulk-upload')} data-id="element-1858">
                    <ListItemIcon sx={{
                  minWidth: 28,
                  color: activePage === 'bulk-upload' ? '#0052CC' : theme.palette.text.secondary,
                  '& svg': {
                    filter: activePage === 'bulk-upload' ? 'drop-shadow(0 0 6px rgba(79, 70, 229, 0.5))' : 'none'
                  }
                }} data-id="element-1859">
                      <Icon icon="lucide:upload" width={16} height={16} data-id="element-1860" />
                    </ListItemIcon>
                    <ListItemText primary="Bulk Upload" sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.85rem',
                    fontWeight: activePage === 'bulk-upload' ? 600 : 500,
                    color: activePage === 'bulk-upload' ? '#0052CC' : theme.palette.text.primary
                  }
                }} data-id="element-1861" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding data-id="element-1862">
                  <ListItemButton sx={{
                pl: 4,
                py: 1,
                borderRadius: 2,
                backgroundColor: activePage === 'bulk-upload-lists' ? '#F0F3F7' : 'transparent',
                position: 'relative',
                '&:hover': {
                  backgroundColor: activePage === 'bulk-upload-lists' ? '#F9FAFB' : 'rgba(79, 70, 229, 0.04)'
                },
                '&::before': activePage === 'bulk-upload-lists' ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: '#0052CC'
                } : {},
                transition: 'all 200ms ease',
                '&:hover': {
                  transform: 'translateX(3px)'
                }
              }} onClick={() => handleNavigation('/branch-users/bulk-upload/lists')} data-id="element-1863">
                    <ListItemIcon sx={{
                  minWidth: 28,
                  color: activePage === 'bulk-upload-lists' ? '#0052CC' : theme.palette.text.secondary,
                  '& svg': {
                    filter: activePage === 'bulk-upload-lists' ? 'drop-shadow(0 0 6px rgba(79, 70, 229, 0.5))' : 'none'
                  }
                }} data-id="element-1864">
                      <Icon icon="lucide:file-text" width={16} height={16} data-id="element-1865" />
                    </ListItemIcon>
                    <ListItemText primary="Bulk Upload Lists" sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.85rem',
                    fontWeight: activePage === 'bulk-upload-lists' ? 600 : 500,
                    color: activePage === 'bulk-upload-lists' ? '#0052CC' : theme.palette.text.primary
                  }
                }} data-id="element-1866" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding data-id="element-1867">
                  <ListItemButton sx={{
                pl: 4,
                py: 1,
                borderRadius: 2,
                backgroundColor: activePage === 'maker-inbox' ? '#F0F3F7' : 'transparent',
                position: 'relative',
                '&:hover': {
                  backgroundColor: activePage === 'maker-inbox' ? '#F9FAFB' : 'rgba(79, 70, 229, 0.04)'
                },
                '&::before': activePage === 'maker-inbox' ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: '#0052CC'
                } : {},
                transition: 'all 200ms ease',
                '&:hover': {
                  transform: 'translateX(3px)'
                }
              }} onClick={() => handleNavigation('/branch-users/maker-inbox')} data-id="element-1868">
                    <ListItemIcon sx={{
                  minWidth: 28,
                  color: activePage === 'maker-inbox' ? '#0052CC' : theme.palette.text.secondary,
                  '& svg': {
                    filter: activePage === 'maker-inbox' ? 'drop-shadow(0 0 6px rgba(79, 70, 229, 0.5))' : 'none'
                  }
                }} data-id="element-1869">
                      <Icon icon="lucide:inbox" width={16} height={16} data-id="element-1870" />
                    </ListItemIcon>
                    <ListItemText primary="Maker Inbox" sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.85rem',
                    fontWeight: activePage === 'maker-inbox' ? 600 : 500,
                    color: activePage === 'maker-inbox' ? '#0052CC' : theme.palette.text.primary
                  }
                }} data-id="element-1871" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding data-id="element-1872">
                  <ListItemButton sx={{
                pl: 4,
                py: 1,
                borderRadius: 2,
                backgroundColor: activePage === 'checker-inbox' ? '#F0F3F7' : 'transparent',
                position: 'relative',
                '&:hover': {
                  backgroundColor: activePage === 'checker-inbox' ? '#F9FAFB' : 'rgba(79, 70, 229, 0.04)'
                },
                '&::before': activePage === 'checker-inbox' ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: '#0052CC'
                } : {},
                transition: 'all 200ms ease',
                '&:hover': {
                  transform: 'translateX(3px)'
                }
              }} onClick={() => handleNavigation('/branch-users/checker-inbox')} data-id="element-1873">
                    <ListItemIcon sx={{
                  minWidth: 28,
                  color: activePage === 'checker-inbox' ? '#0052CC' : theme.palette.text.secondary,
                  '& svg': {
                    filter: activePage === 'checker-inbox' ? 'drop-shadow(0 0 6px rgba(79, 70, 229, 0.5))' : 'none'
                  }
                }} data-id="element-1874">
                      <Icon icon="lucide:check-square" width={16} height={16} data-id="element-1875" />
                    </ListItemIcon>
                    <ListItemText primary="Checker Inbox" sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.85rem',
                    fontWeight: activePage === 'checker-inbox' ? 600 : 500,
                    color: activePage === 'checker-inbox' ? '#0052CC' : theme.palette.text.primary
                  }
                }} data-id="element-1876" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </>}

        {/* Partners Group - only show for maker */}
        {!isChecker && <>
            <ListItem disablePadding sx={{
          mb: 0.5
        }} data-id="element-1877">
              <ListItemButton sx={{
            borderRadius: 2,
            py: 1.2,
            backgroundColor: activePage === 'partners' || activePage === 'partner-users' ? '#F0F3F7' : 'transparent',
            position: 'relative',
            transition: 'all 200ms ease',
            '&:hover': {
              backgroundColor: activePage === 'partners' || activePage === 'partner-users' ? '#F9FAFB' : 'rgba(79, 70, 229, 0.04)'
            },
            '&::before': activePage === 'partners' || activePage === 'partner-users' ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              background: '#0052CC'
            } : {}
          }} onClick={togglePartners} data-id="element-1878">
                <ListItemIcon sx={{
              minWidth: 36,
              color: activePage === 'partners' || activePage === 'partner-users' ? '#0052CC' : theme.palette.text.secondary,
              '& svg': {
                transition: 'all 200ms ease'
              }
            }} data-id="element-1879">
                  <Icon icon="lucide:handshake" width={20} height={20} data-id="element-1880" />
                </ListItemIcon>
                <ListItemText primary="Partners" sx={{
              '& .MuiTypography-root': {
                fontWeight: activePage === 'partners' || activePage === 'partner-users' ? 600 : 500,
                color: activePage === 'partners' || activePage === 'partner-users' ? '#0052CC' : theme.palette.text.primary,
                fontSize: '14px'
              }
            }} data-id="element-1881" />
                {partnersOpen ? <Icon icon="lucide:chevron-up" width={18} height={18} color={theme.palette.text.secondary} data-id="element-1882" /> : <Icon icon="lucide:chevron-down" width={18} height={18} color={theme.palette.text.secondary} data-id="element-1883" />}
              </ListItemButton>
            </ListItem>
            <Collapse in={partnersOpen} timeout="auto" unmountOnExit data-id="element-1884">
              <List component="div" disablePadding sx={{
            ml: 1,
            mb: 0.5
          }} data-id="element-1885">
                <ListItem disablePadding data-id="element-1886">
                  <ListItemButton sx={{
                pl: 4,
                py: 1,
                borderRadius: 2,
                backgroundColor: activePage === 'partners' ? '#F0F3F7' : 'transparent',
                position: 'relative',
                '&:hover': {
                  backgroundColor: activePage === 'partners' ? '#F9FAFB' : 'rgba(79, 70, 229, 0.04)'
                },
                '&::before': activePage === 'partners' ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: '#0052CC'
                } : {},
                transition: 'all 200ms ease',
                '&:hover': {
                  transform: 'translateX(3px)'
                }
              }} onClick={() => handleNavigation('/partners')} data-id="element-1887">
                    <ListItemIcon sx={{
                  minWidth: 28,
                  color: activePage === 'partners' ? '#0052CC' : theme.palette.text.secondary,
                  '& svg': {
                    filter: activePage === 'partners' ? 'drop-shadow(0 0 6px rgba(79, 70, 229, 0.5))' : 'none'
                  }
                }} data-id="element-1888">
                      <Icon icon="lucide:briefcase" width={16} height={16} data-id="element-1889" />
                    </ListItemIcon>
                    <ListItemText primary="Partner Management" sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.85rem',
                    fontWeight: activePage === 'partners' ? 600 : 500,
                    color: activePage === 'partners' ? '#0052CC' : theme.palette.text.primary
                  }
                }} data-id="element-1890" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding data-id="element-1891">
                  <ListItemButton sx={{
                pl: 4,
                py: 1,
                borderRadius: 2,
                backgroundColor: activePage === 'partner-users' ? '#F0F3F7' : 'transparent',
                position: 'relative',
                '&:hover': {
                  backgroundColor: activePage === 'partner-users' ? '#F9FAFB' : 'rgba(79, 70, 229, 0.04)'
                },
                '&::before': activePage === 'partner-users' ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: '#0052CC'
                } : {},
                transition: 'all 200ms ease',
                '&:hover': {
                  transform: 'translateX(3px)'
                }
              }} onClick={() => handleNavigation('/partner-users')} data-id="element-1892">
                    <ListItemIcon sx={{
                  minWidth: 28,
                  color: activePage === 'partner-users' ? '#0052CC' : theme.palette.text.secondary,
                  '& svg': {
                    filter: activePage === 'partner-users' ? 'drop-shadow(0 0 6px rgba(79, 70, 229, 0.5))' : 'none'
                  }
                }} data-id="element-1893">
                      <Icon icon="lucide:user-cog" width={16} height={16} data-id="element-1894" />
                    </ListItemIcon>
                    <ListItemText primary="User Management" sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.85rem',
                    fontWeight: activePage === 'partner-users' ? 600 : 500,
                    color: activePage === 'partner-users' ? '#0052CC' : theme.palette.text.primary
                  }
                }} data-id="element-1895" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </>}

        {/* Sub Agents Group - only show for maker */}
        {!isChecker && <>
            <ListItem disablePadding sx={{
          mb: 0.5
        }} data-id="element-1896">
              <ListItemButton sx={{
            borderRadius: 2,
            py: 1.2,
            backgroundColor: activePage === 'sub-agents' || activePage === 'sub-agent-users' ? '#F0F3F7' : 'transparent',
            position: 'relative',
            transition: 'all 200ms ease',
            '&:hover': {
              backgroundColor: activePage === 'sub-agents' || activePage === 'sub-agent-users' ? '#F9FAFB' : 'rgba(79, 70, 229, 0.04)'
            },
            '&::before': activePage === 'sub-agents' || activePage === 'sub-agent-users' ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              background: '#0052CC'
            } : {}
          }} onClick={toggleSubAgents} data-id="element-1897">
                <ListItemIcon sx={{
              minWidth: 36,
              color: activePage === 'sub-agents' || activePage === 'sub-agent-users' ? '#0052CC' : theme.palette.text.secondary,
              '& svg': {
                transition: 'all 200ms ease'
              }
            }} data-id="element-1898">
                  <Icon icon="lucide:network" width={20} height={20} data-id="element-1899" />
                </ListItemIcon>
                <ListItemText primary="Sub Agents" sx={{
              '& .MuiTypography-root': {
                fontWeight: activePage === 'sub-agents' || activePage === 'sub-agent-users' ? 600 : 500,
                color: activePage === 'sub-agents' || activePage === 'sub-agent-users' ? '#0052CC' : theme.palette.text.primary,
                fontSize: '14px'
              }
            }} data-id="element-1900" />
                {subAgentsOpen ? <Icon icon="lucide:chevron-up" width={18} height={18} color={theme.palette.text.secondary} data-id="element-1901" /> : <Icon icon="lucide:chevron-down" width={18} height={18} color={theme.palette.text.secondary} data-id="element-1902" />}
              </ListItemButton>
            </ListItem>
            <Collapse in={subAgentsOpen} timeout="auto" unmountOnExit data-id="element-1903">
              <List component="div" disablePadding sx={{
            ml: 1,
            mb: 0.5
          }} data-id="element-1904">
                <ListItem disablePadding data-id="element-1905">
                  <ListItemButton sx={{
                pl: 4,
                py: 1,
                borderRadius: 2,
                backgroundColor: activePage === 'sub-agents' ? '#F0F3F7' : 'transparent',
                position: 'relative',
                '&:hover': {
                  backgroundColor: activePage === 'sub-agents' ? '#F9FAFB' : 'rgba(79, 70, 229, 0.04)'
                },
                '&::before': activePage === 'sub-agents' ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: '#0052CC'
                } : {},
                transition: 'all 200ms ease',
                '&:hover': {
                  transform: 'translateX(3px)'
                }
              }} onClick={() => handleNavigation('/sub-agents')} data-id="element-1906">
                    <ListItemIcon sx={{
                  minWidth: 28,
                  color: activePage === 'sub-agents' ? '#0052CC' : theme.palette.text.secondary,
                  '& svg': {
                    filter: activePage === 'sub-agents' ? 'drop-shadow(0 0 6px rgba(79, 70, 229, 0.5))' : 'none'
                  }
                }} data-id="element-1907">
                      <Icon icon="lucide:clipboard-list" width={16} height={16} data-id="element-1908" />
                    </ListItemIcon>
                    <ListItemText primary="Sub Agents Management" sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.85rem',
                    fontWeight: activePage === 'sub-agents' ? 600 : 500,
                    color: activePage === 'sub-agents' ? '#0052CC' : theme.palette.text.primary
                  }
                }} data-id="element-1909" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding data-id="element-1910">
                  <ListItemButton sx={{
                pl: 4,
                py: 1,
                borderRadius: 2,
                backgroundColor: activePage === 'sub-agent-users' ? '#F0F3F7' : 'transparent',
                position: 'relative',
                '&:hover': {
                  backgroundColor: activePage === 'sub-agent-users' ? '#F9FAFB' : 'rgba(79, 70, 229, 0.04)'
                },
                '&::before': activePage === 'sub-agent-users' ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: '#0052CC'
                } : {},
                transition: 'all 200ms ease',
                '&:hover': {
                  transform: 'translateX(3px)'
                }
              }} onClick={() => handleNavigation('/sub-agent-users')} data-id="element-1911">
                    <ListItemIcon sx={{
                  minWidth: 28,
                  color: activePage === 'sub-agent-users' ? '#0052CC' : theme.palette.text.secondary,
                  '& svg': {
                    filter: activePage === 'sub-agent-users' ? 'drop-shadow(0 0 6px rgba(79, 70, 229, 0.5))' : 'none'
                  }
                }} data-id="element-1912">
                      <Icon icon="lucide:user-circle" width={16} height={16} data-id="element-1913" />
                    </ListItemIcon>
                    <ListItemText primary="User Management" sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.85rem',
                    fontWeight: activePage === 'sub-agent-users' ? 600 : 500,
                    color: activePage === 'sub-agent-users' ? '#0052CC' : theme.palette.text.primary
                  }
                }} data-id="element-1914" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </>}
      </List>
      <Box sx={{
      p: 2,
      mt: 'auto',
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-1915">
        <Typography variant="caption" sx={{
        color: 'text.secondary',
        display: 'block',
        textAlign: 'center',
        opacity: 0.7
      }} data-id="element-1916">
          OpenRemit v2.0.0
        </Typography>
      </Box>
    </Box>;
  return <>
      {isMobile ? <Drawer anchor="left" open={open} onClose={onClose} sx={{
      '& .MuiDrawer-paper': {
        width: drawerWidth,
        boxSizing: 'border-box',
        overflow: 'hidden'
      }
    }} data-id="element-1917">
          {drawerContent}
        </Drawer> : <Drawer variant="permanent" sx={{
      width: drawerWidth,
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        width: drawerWidth,
        boxSizing: 'border-box',
        borderRight: '1px solid #E5E7EB',
        overflow: 'hidden'
      }
    }} data-id="element-1918">
          {drawerContent}
        </Drawer>}
    </>;
};
export default Sidebar;